import { authFetch } from '../utils/authFetch';

export interface UserProfileContextBootstrap {
  mePayload: Record<string, unknown>;
}

async function parseJsonSafe<T>(response: Response): Promise<T | null> {
  try {
    return (await response.json()) as T;
  } catch {
    return null;
  }
}

export async function fetchUserProfileContextBootstrap(signal?: AbortSignal): Promise<UserProfileContextBootstrap | null> {
  const sensitiveGetInit = { autoLogout: false, cache: 'no-store' as RequestCache, skipDedupe: true, signal };
  let meResponse = await authFetch('/employee/api/me', sensitiveGetInit);
  if (meResponse.status === 404 || meResponse.status === 403 || meResponse.status === 401) {
    meResponse = await authFetch('/reception/api/me', sensitiveGetInit);
  }
  if (!meResponse.ok) return null;

  const mePayload = await parseJsonSafe<Record<string, unknown>>(meResponse);
  if (!mePayload) return null;

  return { mePayload };
}

export interface ReceptionPendingBadgesResult {
  pendingDanger: number | null;
  pendingAnnouncements: number;
  unauthorized: boolean;
}

export async function fetchReceptionPendingBadges(signal?: AbortSignal): Promise<ReceptionPendingBadgesResult> {
  const sensitiveGetInit = { cache: 'no-store' as RequestCache, skipDedupe: true, signal };
  const [logsRes, announcementsRes] = await Promise.all([
    authFetch('/reception/api/super/logs/meta', sensitiveGetInit),
    authFetch('/reception/api/announcements/pending', sensitiveGetInit),
  ]);

  if (!logsRes.ok) {
    return {
      pendingDanger: null,
      pendingAnnouncements: 0,
      unauthorized: logsRes.status === 401 || logsRes.status === 403,
    };
  }

  const logsMeta = await parseJsonSafe<{ pending_danger?: unknown }>(logsRes);
  const pendingDanger = typeof logsMeta?.pending_danger === 'number' ? logsMeta.pending_danger : null;

  if (!announcementsRes.ok) {
    return {
      pendingDanger,
      pendingAnnouncements: 0,
      unauthorized: announcementsRes.status === 401 || announcementsRes.status === 403,
    };
  }

  const announcementPayload = await parseJsonSafe<{ pending?: unknown[] }>(announcementsRes);
  return {
    pendingDanger,
    pendingAnnouncements: Array.isArray(announcementPayload?.pending) ? announcementPayload.pending.length : 0,
    unauthorized: false,
  };
}

export type ReceptionRouteLookupErrorCode = 'unauthorized' | 'forbidden' | 'not_found' | 'timeout' | 'network' | 'unknown';

export class ReceptionRouteLookupError extends Error {
  code: ReceptionRouteLookupErrorCode;

  constructor(code: ReceptionRouteLookupErrorCode, message?: string) {
    super(message ?? code);
    this.code = code;
  }
}

export interface ReceptionStudentRoutePayload {
  id?: number;
  full_name?: string;
  student_name?: string;
  name?: string;
  email?: string;
  student_code?: string;
  intended_course?: string;
  courses?: string[];
  student?: {
    id?: number;
    full_name?: string;
    student_name?: string;
    name?: string;
    email?: string;
    student_code?: string;
    intended_course?: string;
    courses?: string[];
  };
}

export async function fetchReceptionStudentRoutePayload(
  sid: number,
  signal?: AbortSignal,
): Promise<ReceptionStudentRoutePayload> {
  const timeoutController = new AbortController();
  const timeoutId = window.setTimeout(() => timeoutController.abort(), 30_000);
  const abortListener = () => timeoutController.abort();
  signal?.addEventListener('abort', abortListener);

  try {
    const response = await authFetch(`/reception/api/students/${sid}?view=invoice`, {
      cache: 'no-store',
      skipDedupe: true,
      signal: timeoutController.signal,
    });

    if (response.status === 401) throw new ReceptionRouteLookupError('unauthorized');
    if (response.status === 403) throw new ReceptionRouteLookupError('forbidden');
    if (response.status === 404) throw new ReceptionRouteLookupError('not_found');
    if (!response.ok) throw new ReceptionRouteLookupError('unknown');

    const payload = await parseJsonSafe<ReceptionStudentRoutePayload>(response);
    return payload ?? {};
  } catch (error) {
    if (error instanceof ReceptionRouteLookupError) throw error;
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new ReceptionRouteLookupError('timeout');
    }
    throw new ReceptionRouteLookupError('network');
  } finally {
    window.clearTimeout(timeoutId);
    signal?.removeEventListener('abort', abortListener);
  }
}
